import { Stack } from "@mui/material";
import _ from "lodash";
import type React from "react";
import { useMemo, useState } from "react";
import { filterText } from "../../dashboard/utils";
import type { IVideo } from "../../entities/video.entity";
import { useVideoPlayerStore } from "../../utils/store";
import { CenterBigButtons } from "../SubPageLayout";
import type { FilterByGroup } from "../pages/CategoryRender";
import { usePlayerData } from "../usePlayerData";
import { BigButtons } from "./BigButtons";
import { BreadCrumb } from "./BreadCrumb";
import { ContentContainer } from "./ContentContainer";
import { defineButton } from "./DynamicBigButton";
import { LinksPromptWrapper } from "./LinksPromptWrapper";
import { LoadingVideos } from "./LoadingVideos";
import { SearchInput } from "./SearchInput";
import { VideosButtons } from "./VideosButtons";

export const VideosFiltered = (props: {
	filter: string;
	groupFilter: FilterByGroup;
}) => {
	const playerData = usePlayerData();

	const group = playerData.queryGroupBy({
		id: props.groupFilter.groupId,
		name: props.groupFilter.groupName,
	});
	const filteredList = useMemo(() => {
		if (!group) {
			return [];
		}

		const filtered =
			group?.videos?.filter((video) => {
				return filterText(video.title, props.filter);
			}) || [];

		// Sort by episode_number if available
		return _.orderBy(
			filtered,
			[
				(video) => (video.episode_number ? 0 : 1), // Videos with episode_number first
				"episode_number", // Then sort by episode_number
			],
			["asc", "asc"],
		);
	}, [group, props.filter]);

	if (playerData.query.isLoading) {
		return <LoadingVideos breadcrumb={[]} />;
	}

	if (playerData.query.isError) {
		return <div>Erro ao carregar dados do player</div>;
	}

	const isEmpty = filteredList.length === 0;

	if (isEmpty) {
		return (
			<div className="bg-black bg-opacity-50 rounded-lg p-4 w-full">
				<h2 className="uppercase text-xl md:text-2xl pb-1 font-semibold">
					{`Sem resultados para "${props.filter}"`}
				</h2>
			</div>
		);
	}

	return <VideosButtons videos={filteredList} />;
};

export const useVideoOpen = () => {
	const videoPlayer = useVideoPlayerStore();
	const handleVideoClick = async (video: IVideo) => {
		videoPlayer.setLoop(false);
		await videoPlayer.playVideo({
			originalUrl: video.video.url,
			id: video.id,
			title: video.title,
		});
	};

	return handleVideoClick;
};

export const VideosListHeader = (props: {
	onChange: (value: string) => void;
	searchValue?: string;
	breadcrumb: string[];
}) => {
	return (
		<Stack direction={"row"} alignItems={"center"}>
			<BreadCrumb steps={props.breadcrumb} />
			<SearchInput onChange={props.onChange} value={props.searchValue} />
		</Stack>
	);
};

export const ErrorMessage = ({ message }: { message: string }) => {
	return (
		<div className="bg-red-800 text-white bg-opacity-50 rounded-lg p-4 w-full">
			<h2 className="uppercase text-xl md:text-2xl pb-1 font-semibold">Erro</h2>
			<p>{message}</p>
		</div>
	);
};

export const CategoryVideos = (props: {
	breadcrumb: string[];
	categoryId?: string;
	groupFilter: FilterByGroup;
	suffixButton?: React.ReactNode;
	subCategoryPathIds?: string[];
}) => {
	const playerData = usePlayerData();

	if (playerData.query.isLoading) {
		return <LoadingVideos breadcrumb={props.breadcrumb} />;
	}

	if (playerData.query.isError) {
		return <div>Erro ao carregar dados do player</div>;
	}

	const activeGroup = playerData.queryGroupBy({
		id: props.groupFilter.groupId,
		name: props.groupFilter.groupName,
	});

	if (!activeGroup) {
		return (
			<ErrorMessage
				message={`Grupo não encontrado: ${props.groupFilter.groupName}`}
			/>
		);
	}

	if (!props.categoryId) {
		return (
			<ErrorMessage
				message={`Category ID não informado: ${props.categoryId}`}
			/>
		);
	}

	const activeCategory = _.find(activeGroup.categories, {
		id: props.categoryId,
	});

	if (!activeCategory) {
		return (
			<ErrorMessage message={`Categoria não encontrada: ${props.categoryId}`} />
		);
	}

	const selectedPathIds = props.subCategoryPathIds || [];
	let currentNode = null as
		| (typeof activeCategory.subCategories)[number]
		| null;
	let currentLevel = activeCategory.subCategories;

	for (const id of selectedPathIds) {
		const found = currentLevel.find((item) => item.id === id);
		if (!found) {
			return <ErrorMessage message={`Subcategoria não encontrada: ${id}`} />;
		}
		currentNode = found;
		currentLevel = found.subCategories || [];
	}

	const children = currentNode
		? currentNode.subCategories || []
		: activeCategory.subCategories;
	const isPlaceholderName = (name: string) => name.trim() === "-";
	const placeholderChildren = children.filter((child) =>
		isPlaceholderName(child.name),
	);
	const displayChildren = children.filter(
		(child) => !isPlaceholderName(child.name),
	);
	const hasChildren = displayChildren.length > 0;
	const childrenHaveChildren = displayChildren.some(
		(child) => (child.subCategories?.length || 0) > 0,
	);
	const childrenAreLeaves =
		hasChildren &&
		displayChildren.every((child) => (child.subCategories?.length || 0) === 0);
	const placeholderVideos = placeholderChildren.flatMap(
		(child) => child.videos || [],
	);
	const currentVideos = _.uniqBy(
		[...(currentNode?.videos || []), ...placeholderVideos],
		"id",
	);
	const hasCurrentVideos = currentVideos.length > 0;

	if (hasChildren && (childrenHaveChildren || !currentNode)) {
		return (
			<div className="w-full mx-auto space-y-6 text-white">
				{hasCurrentVideos ? (
					<div>
						<VideosButtons videos={currentVideos} />
					</div>
				) : null}
				<CenterBigButtons>
					<BigButtons
						buttons={displayChildren.map((subCategory) => {
							return defineButton({
								label: subCategory.name,
								navigateTo: subCategory.id,
							});
						})}
						suffix={
							selectedPathIds.length === 0 ? props.suffixButton : undefined
						}
					/>
				</CenterBigButtons>
			</div>
		);
	}

	return (
		<div className="w-full mx-auto space-y-3 text-white">
			{(() => {
				if (currentNode && hasChildren && childrenAreLeaves) {
					return (
						<>
							{hasCurrentVideos ? (
								<div>
									<VideosButtons videos={currentVideos} />
								</div>
							) : null}
							{displayChildren.map((leaf) => (
								<div key={leaf.id}>
									<h2 className="uppercase text-xl md:text-2xl pb-1 font-semibold text-condor-blue">
										{leaf.name}
									</h2>
									<VideosButtons videos={leaf.videos} />
								</div>
							))}
						</>
					);
				}

				if (currentNode) {
					return (
						<div key={currentNode.id}>
							<VideosButtons videos={currentVideos} />
						</div>
					);
				}

				return activeCategory.subCategories.map((subCategory) => (
					<div key={subCategory.id}>
						<VideosButtons videos={subCategory.videos} />
					</div>
				));
			})()}
		</div>
	);
};

export const FilterableListController = (props: {
	children?: React.ReactNode;
	breadcrumb: string[];
	groupFilter: FilterByGroup;
	categoryId?: string;
	subCategoryId?: string;
	subCategoryPaths?: string[];
}) => {
	const [filter, setFilter] = useState("");
	const isFiltering = filter.length > 0;

	return (
		<ContentContainer>
			<LinksPromptWrapper
				groupId={props.groupFilter?.groupId}
				groupName={props.groupFilter?.groupName}
				categoryId={props.categoryId}
				subCategoryId={props.subCategoryId}
				subCategoryPaths={props.subCategoryPaths}
			>
				<VideosListHeader
					key="same-header"
					onChange={setFilter}
					breadcrumb={props.breadcrumb}
					searchValue={filter}
				/>
				<div className="py-12 px-2 text-white">
					{isFiltering || !props.children ? (
						<VideosFiltered filter={filter} groupFilter={props.groupFilter} />
					) : (
						props.children
					)}
				</div>
			</LinksPromptWrapper>
		</ContentContainer>
	);
};
